import { IconCheckCircle, IconWarning, IconWarningOctagon } from "@decibeltrade/react-icons";
import { Spinner } from "@decibeltrade/web-core/components";
import { JSX, ReactNode, useId } from "react";
import { css, cva, cx, RecipeVariantProps } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { FormControlVariants } from "@/recipes/form-control";

import { Skeleton } from "../skeleton";
import { TooltipText } from "../tooltip";

export interface FormControlProps {
  /** The ID of the form control element. */
  id: string;
  /** The ID of the element that describes the form control element. */
  "aria-describedby"?: string;
}

export interface FormFieldProps {
  /** The label text to be displayed above the form control element */
  label?: string;
  /**
   * If true, the label is read by assistive tech but not drawn.
   *
   * For controls whose surroundings already say what they are — a heading directly above, say —
   * where repeating it on screen is noise but dropping it would leave the control unnamed.
   */
  labelHidden?: boolean;
  /** Secondary text to be displayed to the right of the label about the form control element */
  labelSecondary?: React.ReactNode;
  /** If true, the labelSecondary will be highlighted */
  highlightSecondaryLabel?: boolean;
  /** The description text to be displayed below the form control element */
  description?: string;
  /** Secondary description text to be displayed on the rightside of the description */
  secondaryDescription?: string;
  /** If true, the description will be highlighted */
  highlightDescription?: boolean;
  /** If true, the description will display as a skeleton */
  loadingDescription?: boolean;
  /** If true, the secondary description will display as a skeleton */
  loadingSecondaryDescription?: boolean;
  /** If a string is provided, the form will display the text error instead of the description */
  error?: string;
  /** The size of the form field */
  size?: FormControlVariants["size"];
  /** The validation state of the form field */
  state?: FormControlVariants["state"];
  /** If true, the label and description will use the disabled styled. */
  disabled?: boolean;
  /** A function that returns the form control element to be decorated with a label and/or description */
  children: (formControlProps: FormControlProps) => ReactNode;
  /** Class name for applying additional styles to the wrapper element */
  className?: string;
  /** If true, the form field will display a required asterisk */
  required?: boolean;
  /** A tooltip to be displayed next to the form field */
  labelTooltip?: string;
  /** A tooltip to be displayed next to the labelsecondary field */
  labelSecondaryTooltip?: React.ReactNode;
  /** A tooltip to be displayed on the secondary description */
  secondaryDescriptionTooltip?: React.ReactNode;
}

/**
 * This component allows you to add an accessible label and/or description to any form control.
 *
 * The `children` prop of this component is a function that supplies you with render props to spread
 * onto your form control element to ensure that the label and/or description are properly linked
 * to the control element.
 */
export function FormField({
  label,
  labelHidden,
  labelSecondary,
  highlightSecondaryLabel,
  description,
  secondaryDescription,
  highlightDescription,
  loadingDescription,
  loadingSecondaryDescription,
  error,
  size = "md",
  state: _state = "default",
  disabled,
  children,
  className,
  required,
  labelTooltip,
  labelSecondaryTooltip,
  secondaryDescriptionTooltip,
}: FormFieldProps) {
  const controlId = useId();
  const descriptionId = useId();

  const state = error ? "error" : _state;

  const formControlProps = {
    id: controlId,
    "aria-describedby": description ? descriptionId : undefined,
    state,
  };

  const labelNode = !!label && (
    <label htmlFor={controlId} className={labelStyles({ disabled, size, hidden: labelHidden })}>
      {labelTooltip ? (
        <TooltipText content={labelTooltip} placement="top">
          {label}
        </TooltipText>
      ) : (
        label
      )}
      {required && <>*</>}
    </label>
  );

  const labelSecondaryNode = !!labelSecondary && (
    <p className={labelSecondaryStyles({ disabled, size, highlight: highlightSecondaryLabel })}>
      {labelSecondaryTooltip ? (
        <TooltipText content={labelSecondaryTooltip}>{labelSecondary}</TooltipText>
      ) : (
        labelSecondary
      )}
    </p>
  );

  let descriptionVariant: NonNullable<RecipeVariantProps<typeof descriptionStyles>>["variant"];
  if (highlightDescription) descriptionVariant = "highlight";
  if (error) descriptionVariant = "error";

  const descriptionNode = (typeof description !== "undefined" || !!error) && (
    <div
      id={descriptionId}
      className={descriptionStyles({ disabled, size, variant: descriptionVariant })}
    >
      {descriptionIcon[state]}
      {loadingDescription ? (
        <Skeleton className={descriptionSkeletonStyles({ size })} />
      ) : (
        <p>{error || description}</p>
      )}
    </div>
  );

  const secondaryDescriptionNode =
    typeof secondaryDescription !== "undefined" &&
    (loadingSecondaryDescription ? (
      <Skeleton className={descriptionSkeletonStyles({ size })} />
    ) : (
      <p
        className={descriptionStyles({
          disabled,
          size,
          variant: descriptionVariant,
          alignRight: true,
        })}
      >
        {secondaryDescriptionTooltip ? (
          <TooltipText content={secondaryDescriptionTooltip} placement="bottom">
            {secondaryDescription}
          </TooltipText>
        ) : (
          secondaryDescription
        )}
      </p>
    ));

  return (
    <div className={cx(stack({ w: "full", gap: "4" }), className)}>
      {labelSecondaryNode ? (
        <div className={flex({ gap: "4", justifyContent: "space-between", alignItems: "center" })}>
          {labelNode}
          {labelSecondaryNode}
        </div>
      ) : (
        labelNode
      )}
      {children(formControlProps)}
      <div className={flex({ justifyContent: "space-between", alignItems: "center" })}>
        {descriptionNode}
        {secondaryDescriptionNode}
      </div>
    </div>
  );
}

const labelStyles = cva({
  base: { color: "text.secondary" },
  variants: {
    disabled: {
      true: {
        color: "text.disabled",
      },
    },
    hidden: {
      true: { srOnly: true },
    },
    size: {
      sm: { textStyle: { base: "body.lg", desktop: "body.sm" }, px: "8" },
      md: { textStyle: { base: "body.lg", desktop: "body.md" }, px: "12" },
      lg: { textStyle: { base: "body.xl", desktop: "body.lg" }, px: "12" },
    },
  },
  defaultVariants: { size: "md" },
});

const labelSecondaryStyles = cva({
  base: { color: "text.tertiary" },
  variants: {
    disabled: {
      true: {
        color: "text.disabled",
      },
    },
    highlight: {
      true: {
        color: "text.highlight",
      },
    },
    size: {
      sm: { textStyle: { base: "body.lg", desktop: "body.sm" }, px: "8" },
      md: { textStyle: { base: "body.lg", desktop: "body.md" }, px: "12" },
      lg: { textStyle: { base: "body.xl", desktop: "body.lg" }, px: "12" },
    },
  },
  defaultVariants: { size: "md" },
});

const descriptionStyles = cva({
  base: {
    display: "flex",
    gap: "4",
    color: "text.tertiary",
    alignItems: "center",
  },
  variants: {
    disabled: {
      true: {
        color: "text.disabled",
      },
    },
    variant: {
      highlight: {
        color: "text.highlight",
      },
      error: {
        color: "text.negative",
      },
    },
    alignRight: {
      true: {
        textAlign: "right",
      },
    },
    size: {
      sm: {
        px: "8",
        "& > svg": { h: "16", w: "16", flexShrink: "0" },
        "& > p": { textStyle: { base: "body.lg", desktop: "body.sm" } },
      },
      md: {
        px: "12",
        "& > svg": { h: "16", w: "16", flexShrink: "0" },
        "& > p": { textStyle: { base: "body.lg", desktop: "body.md" } },
      },
      lg: {
        px: "12",
        "& > svg": { h: "20", w: "20", flexShrink: "0" },
        "& > p": { textStyle: { base: "body.xl", desktop: "body.lg" } },
      },
    },
  },
  defaultVariants: { size: "md" },
});

const descriptionSkeletonStyles = cva({
  base: {
    width: "72",
    mx: "12",
  },
  variants: {
    size: {
      sm: {
        h: { base: "[{lineHeights.18}]", desktop: "[{lineHeights.14}]" },
      },
      md: {
        h: { base: "[{lineHeights.18}]", desktop: "[{lineHeights.16}]" },
      },
      lg: {
        h: { base: "[{lineHeights.20}]", desktop: "[{lineHeights.18}]" },
      },
    },
  },
});

const descriptionIcon: Record<NonNullable<FormControlVariants["state"]>, JSX.Element | null> = {
  default: null,
  loading: <Spinner variant="secondary" size="sm" className={css({ p: "1" })} />,
  error: <IconWarningOctagon />,
  warning: <IconWarning />,
  success: <IconCheckCircle />,
};

export const Error = ({
  error,
  className,
  size,
}: {
  error: string;
  className?: string;
  size?: FormControlVariants["size"];
}) => {
  return (
    <span className={cx(descriptionStyles({ variant: "error", size }), className)}>
      <IconWarningOctagon />
      {error}
    </span>
  );
};
